import * as React from "react";
import { useState } from "react";
import apiClient from "@/api-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Avatar, Grid, IconButton } from "@mui/material";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { KeyIcon, XIcon } from "@phosphor-icons/react";
import { ArrowRightIcon } from "@phosphor-icons/react/dist/ssr/ArrowRight";
import { EnvelopeIcon } from "@phosphor-icons/react/dist/ssr/Envelope";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { z as zod } from "zod";

import { useDialog } from "@/hooks/use-dialog";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useAuth } from "@/components/core/auth-provider";
import { CustomFormHelperError } from "@/components/customFormHelper/custom-form-helper-error";
import { DeleteModal } from "@/components/deleteModal/delete-modal";

const verifyCodeSchema = zod.object({
	code: zod.string().min(1, { message: "Code is required" }),
});

type VerifyCodeValues = zod.infer<typeof verifyCodeSchema>;

export function TwoFactorSettings(): React.JSX.Element {
	const { user, setUser } = useAuth();
	const otpEnabled = user?.otpEnabled ?? false;
	const isMobile = useMediaQuery("down", "md");

	const [showVerificationDialog, setShowVerificationDialog] = useState(false);
	const [setupData, setSetupData] = useState<{ qr: string; link: string; challangeId: string } | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isVerifying, setIsVerifying] = useState(false);

	const {
		open: isDisableGoogle2FAModalOpen,
		handleClose: handleDisableGoogle2FAModalClose,
		handleOpen: handleDisableGoogle2FAModalOpen,
	} = useDialog<boolean>();

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<VerifyCodeValues>({
		resolver: zodResolver(verifyCodeSchema),
		defaultValues: {
			code: "",
		},
	});

	const handleSetupGoogle2FA = async () => {
		setIsLoading(true);
		try {
			const response = await apiClient.post("/users/me/otp");
			setSetupData(response.data);
			setShowVerificationDialog(true);
		} catch (error: any) {
			toast.error(error?.response?.data?.message || "Failed to set up Google Authenticator");
		} finally {
			setIsLoading(false);
		}
	};

	const handleDisableGoogle2FA = async () => {
		setIsLoading(true);
		try {
			const response = await apiClient.delete("/users/me/otp", {
				withCredentials: true,
			});
			setUser(response.data);
			toast.success("Google Authenticator disabled");
		} catch (error: any) {
			toast.error(error?.response?.data?.message || "Failed to disable Google Authenticator");
		} finally {
			setIsLoading(false);
		}
	};

	const onVerifySubmit = async (values: VerifyCodeValues) => {
		setIsVerifying(true);
		try {
			const response = await apiClient.post(
				"/users/me/otp/verify",
				{
					code: values.code,
					challangeId: setupData?.challangeId,
				},
				{
					withCredentials: true,
				}
			);
			setShowVerificationDialog(false);
			setSetupData(null);
			reset();
			setUser(response.data);
			toast.success("Google Authenticator verified and enabled");
		} catch (error: any) {
			toast.error(error?.response?.data?.message || "Failed to verify code");
		} finally {
			setIsVerifying(false);
		}
	};

	const handleCloseVerificationDialog = () => {
		setShowVerificationDialog(false);
		setTimeout(() => {
			setSetupData(null);
			reset();
		}, 300);
	};

	return (
		<>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={3}
						sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
					>
						<Box sx={{ flex: "1 1 auto" }}>
							<Typography variant="h4">Two-factor authentication</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
								Manage your security settings
							</Typography>
						</Box>
					</Stack>
					<Card>
						<CardHeader
							avatar={
								<Avatar>
									<KeyIcon fontSize="var(--Icon-fontSize)" />
								</Avatar>
							}
							title="Two-factor authentication settings"
						/>
						<CardContent>
							<Stack spacing={3}>
								<Grid container spacing={3}>
									<Grid
										size={{
											xl: 6,
											xs: 12,
										}}
									>
										<Card sx={{ height: "100%" }} variant="outlined">
											<CardContent>
												<Stack spacing={4}>
													<Stack spacing={1}>
														<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
															<Box
																sx={{
																	bgcolor: "var(--mui-palette-success-main)",
																	borderRadius: "50%",
																	display: "block",
																	height: "8px",
																	width: "8px",
																}}
															/>
															<Typography color="success.main" variant="body2">
																On
															</Typography>
														</Stack>
														<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
															<EnvelopeIcon fontSize="20px" />
															<Typography variant="subtitle2">Email 2FA</Typography>
														</Stack>
														<Typography color="text.secondary" variant="body2">
															You can receive security codes via email when signing in, even if Google Authenticator is
															enabled.
														</Typography>
													</Stack>
												</Stack>
											</CardContent>
										</Card>
									</Grid>

									<Grid
										size={{
											xl: 6,
											xs: 12,
										}}
									>
										<Card sx={{ height: "100%" }} variant="outlined">
											<CardContent>
												<Stack spacing={4}>
													<Stack spacing={1}>
														<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
															<Box
																sx={{
																	bgcolor: otpEnabled
																		? "var(--mui-palette-success-main)"
																		: "var(--mui-palette-error-main)",
																	borderRadius: "50%",
																	display: "block",
																	height: "8px",
																	width: "8px",
																}}
															/>
															<Typography color={otpEnabled ? "success.main" : "error"} variant="body2">
																{otpEnabled ? "On" : "Off"}
															</Typography>
														</Stack>
														<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
															<KeyIcon fontSize="20px" />
															<Typography variant="subtitle2">Google Authenticator</Typography>
														</Stack>
														<Typography color="text.secondary" variant="body2">
															{otpEnabled
																? "Active. You can use Google Authenticator for two-factor authentication. Email 2FA remains available as an alternative."
																: "Generate one-time security codes using an authenticator app. Email 2FA will remain available."}
														</Typography>
													</Stack>
													<div>
														{otpEnabled ? (
															<Button
																variant="outlined"
																color="error"
																onClick={() => handleDisableGoogle2FAModalOpen()}
																disabled={isLoading}
															>
																Disable
															</Button>
														) : (
															<Button
																endIcon={<ArrowRightIcon />}
																variant="contained"
																onClick={handleSetupGoogle2FA}
																disabled={isLoading}
															>
																Set up authenticator
															</Button>
														)}
													</div>
												</Stack>
											</CardContent>
										</Card>
									</Grid>
								</Grid>
								<Alert severity="info">
									Email 2FA is always active and available. You can optionally set up Google Authenticator as an
									additional authentication method.
								</Alert>
							</Stack>
						</CardContent>
					</Card>

					<DeleteModal
						open={isDisableGoogle2FAModalOpen}
						onClose={handleDisableGoogle2FAModalClose}
						onDelete={handleDisableGoogle2FA}
						title="Confirm Google Authenticator disable?"
						description="Are you sure you want to disable Google Authenticator? Email 2FA will remain available and active."
						warningText="Once disabled, you will need to set up Google Authenticator again if you want to use it in the future."
						deleteText="Disable"
					/>

					<Dialog open={showVerificationDialog} onClose={handleCloseVerificationDialog} maxWidth="sm" fullWidth>
						<DialogTitle>
							Set up Google Authenticator
							<IconButton
								color="inherit"
								onClick={handleCloseVerificationDialog}
								sx={{ position: "absolute", right: 8, top: 8 }}
							>
								<XIcon width={20} height={20} />
							</IconButton>
						</DialogTitle>
						<DialogContent>
							<Stack spacing={3} sx={{ mt: 1 }}>
								{setupData && (
									<>
										<Alert severity="info">
											Scan this QR code with your authenticator app.
											{isMobile && " You can tap the QR code to open the link."}
										</Alert>

										<Box sx={{ display: "flex", justifyContent: "center", p: 2, bgcolor: "background.paper" }}>
											<a href={setupData.link} target="_blank" rel="noopener noreferrer" style={{ cursor: "pointer" }}>
												<img src={setupData.qr} alt="QR Code" style={{ maxWidth: "200px", height: "auto" }} />
											</a>
										</Box>

										<Alert severity="warning">
											After scanning the QR code, enter the verification code from your authenticator app below.
										</Alert>
									</>
								)}

								<FormControl error={!!errors.code} fullWidth>
									<InputLabel htmlFor="code">Verification Code</InputLabel>
									<Controller
										control={control}
										name="code"
										render={({ field }) => (
											<OutlinedInput
												{...field}
												id="code"
												type="text"
												placeholder="Enter code"
												label="Verification Code"
											/>
										)}
									/>
									{errors.code?.message ? <CustomFormHelperError text={errors.code.message} /> : null}
									<FormHelperText>Enter the code from your authenticator app</FormHelperText>
								</FormControl>
							</Stack>
						</DialogContent>
						<DialogActions sx={{ borderTop: "1px solid var(--mui-palette-divider)", py: 2, px: 3 }}>
							<Button onClick={handleCloseVerificationDialog}>Cancel</Button>
							<Button onClick={handleSubmit(onVerifySubmit)} variant="contained" disabled={isVerifying}>
								Verify
							</Button>
						</DialogActions>
					</Dialog>
				</Stack>
			</Box>
		</>
	);
}
